import { Close as CloseIcon } from "@mui/icons-material";
import {
  IconButton,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material";
import { useField } from "formik";
import React from "react";

interface Props extends Omit<
  MuiTextFieldProps,
  "value" | "onChange" | "onBlur" | "error" | "helperText" | "component"
> {
  name: string;
  clearable?: boolean;
  helperText?: React.ReactNode;
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
}

export const TextField = ({
  clearable,
  helperText,
  value: valueProp,
  onChange: onChangeProp,
  onBlur: onBlurProp,
  slotProps,
  ...props
}: Props) => {
  const [{ value, onChange, onBlur }, { touched, error }, { setValue }] =
    useField(props.name);
  const inputSlotProps =
    typeof slotProps?.input === "function" ? undefined : slotProps?.input;
  return (
    <MuiTextField
      value={valueProp ?? value ?? ""} // should not be null, otherwise changing from controlled to uncontrolled
      onChange={onChangeProp ?? onChange}
      onBlur={args => {
        onBlur?.(args);
        onBlurProp?.(args);
      }}
      error={touched && Boolean(error)}
      helperText={(touched && error) || helperText}
      {...props}
      slotProps={{
        ...slotProps,
        input: {
          ...inputSlotProps,
          ...(clearable && {
            endAdornment: (
              <IconButton
                className="clear-adornment"
                size="small"
                color={"default"}
                onClick={async () => {
                  await setValue("");
                }}
                aria-label="Clear"
                sx={{ visibility: "hidden" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ),
            sx: [
              {
                "&.MuiInputBase-adornedEnd:hover .clear-adornment": {
                  visibility: value ? "visible" : "hidden",
                },
              },
              ...(Array.isArray(inputSlotProps?.sx)
                ? inputSlotProps.sx
                : [inputSlotProps?.sx]),
            ],
          }),
        },
      }}
    />
  );
};
